import * as React from 'react'
import * as PropTypes from 'prop-types'
import * as PapaParse from 'papaparse'
import Encoding from 'encoding-japanese'

export interface IFileInfo {
  name: string
  size: number
  type: string
}

export interface CSVReaderProps {
  accept?: string
  cssClass?: string
  cssInputClass?: string
  inputId?: string
  inputStyle?: object
  label?: string | React.ReactNode
  onFileLoaded: (data: Array<any>, fileInfo: IFileInfo) => any
  parserOptions?: PapaParse.ParseConfig
  disabled?: boolean
}

export const CSVReader: React.FC<CSVReaderProps> = ({
  accept = '.csv, text/csv',
  cssClass = 'csv-reader-input',
  cssInputClass = 'csv-input',
  inputId = 'react-csv-reader-input',
  inputStyle = {},
  label,
  onFileLoaded,
  parserOptions = {} as PapaParse.ParseConfig,
  disabled = false,
}) => {
  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const reader: FileReader = new FileReader()
    const files: FileList = e.target.files!
    if (files.length > 0) {
      const fileInfo: IFileInfo = {
        name: files[0].name,
        size: files[0].size,
        type: files[0].type,
      }

      reader.onload = async (_event: Event) => {
        // regarded as an 8-bit unsigned integer value array
        // @ts-ignore
        const array = new Uint8Array(reader.result)
        reader.readAsText(files[0], Encoding.detect(array))
        let loadCsvRows = await fileToCsv(reader)

        if (!loadCsvRows.length) {
          // Re-read files containing JIS when they could not be retrieved because they may be processed by UNICODDE
          reader.readAsText(files[0], 'SJIS')
          loadCsvRows = await fileToCsv(reader)
        }

        onFileLoaded(loadCsvRows, fileInfo)
      }
      reader.readAsArrayBuffer(files[0])
    }
  }

  const fileToCsv = (reader: FileReader) => {
    return new Promise<object[]>(resolve => {
      reader.onload = (_event: Event) => {
        const csvData = PapaParse.parse(
          reader.result as string,
          Object.assign(parserOptions, {
            error: e => console.error(e),
            encoding: 'UTF8',
          }),
        )
        resolve(csvData.data ? csvData.data : [])
      }
    })
  }

  return (
    <div className={cssClass}>
      {label && (
        <label htmlFor={inputId} style={{ cursor: 'pointer', padding: '10px 0', fontSize: '12px' }}>
          {label}
        </label>
      )}
      <input
        className={cssInputClass}
        type="file"
        id={inputId}
        style={inputStyle}
        accept={accept}
        onClick={e => (e.currentTarget.value = '')}
        onChange={e => handleChangeFile(e)}
        disabled={disabled}
      />
    </div>
  )
}

CSVReader.propTypes = {
  accept: PropTypes.string,
  cssClass: PropTypes.string,
  cssInputClass: PropTypes.string,
  inputId: PropTypes.string,
  inputStyle: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onFileLoaded: PropTypes.func.isRequired,
  parserOptions: PropTypes.object,
  disabled: PropTypes.bool,
}
